import type {
  GenerateQuotationPayloadSchema,
  QuotationSurveyPrepopulationLogicSchema,
  QuotationSurveyPrepopulationRuleSchema,
  QuotationSurveyRecursiveCopySettingsSchema,
  ServicesConfigurationsWithIdSchema,
  ServiceSurveyResponsesWithIdSchema,
  ServiceSurveysWithIdSchema,
} from "@feature/types";

import type {SurveyOutputsType} from "@feature/surveys/types"

import {AxiosResponse} from "axios";

import evaluateBlockDependencies from "@utils/evaluateBlockDependencies";
import convertSurveyResponses from "@utils/convertSurveyResponses";
import prepareConfigurationStep from "@utils/prepareConfigurationStep";

type GetServiceConfiguration = (
  serviceId: number,
  version: number
) => Promise<AxiosResponse<ServicesConfigurationsWithIdSchema>>;

type GetResponses = (
  surveyId: number
) => Promise<ServiceSurveyResponsesWithIdSchema[]>;

type GetConnectionResults = (
  surveyId: number
) => Promise<any>;

type BlockRulesMapping = {
  [key: number]: {
    rule?: QuotationSurveyPrepopulationRuleSchema;
    copy_settings?: QuotationSurveyRecursiveCopySettingsSchema[];
    block: ServicesConfigurationsWithIdSchema;
    logic: QuotationSurveyPrepopulationLogicSchema;
  };
};

/**
 * For each question in the destinationSurvey evaluate the prepopulation logic
 *
 * the prepopulation logic is evaluated depending on the selected sourceSurveys
 * for dynamic rules we need to check if the dependencies match on the sourceSurvey responses
 */
export async function mapQuestionLogics(
  destinationServiceConfiguration: ServicesConfigurationsWithIdSchema[],
  sourceSurveys: ServiceSurveysWithIdSchema[],
  getResponses: GetResponses,
  getServiceConfiguration: GetServiceConfiguration,
  getConnectionResults: GetConnectionResults
) {
  const blockRulesMapping: BlockRulesMapping = {};
  await Promise.all(
    destinationServiceConfiguration.map(
      async (block: ServicesConfigurationsWithIdSchema) => {
        const logic = getPrepopulationLogic(block);
        if (!logic) {
          return;
        }
        if (logic.type === "simple") {
          const rule = await getMatchingRule(
            logic,
            sourceSurveys,
            getResponses,
            getServiceConfiguration,
            getConnectionResults,
            block
          ) as QuotationSurveyPrepopulationRuleSchema | undefined;
          if (!rule) {
            return;
          }
          blockRulesMapping[block.id] = {
            block,
            rule,
            logic,
          };
        } else if (logic.type === "recursive_copy") {
          const copy_settings = await getMatchingRule(
            logic,
            sourceSurveys,
            getResponses,
            getServiceConfiguration,
            getConnectionResults,
            block
          ) as QuotationSurveyRecursiveCopySettingsSchema[] | undefined;
          if (copy_settings === undefined || copy_settings.length === 0) {
            return;
          }
          blockRulesMapping[block.id] = {
            block,
            copy_settings,
            logic,
          };
        } else {
          throw new Error("Malformed prepopulation logic, unknown type");
        }
      }
    )
  );
  return blockRulesMapping;
}

/**
 * Get generate_quotation paypload based on each block specified rule
 * Ideally the rule is picked from the mapQuestionLogics output
 * so we have a 1-1 block-rule mapping
 */
export function getGenerateQuotationPayload(
  blockRulesMapping: BlockRulesMapping,
  designSurveys: ServiceSurveysWithIdSchema[],
  designSurveyValues: {[key: number]: SurveyOutputsType},
): GenerateQuotationPayloadSchema {
  const quotations_questions_values = Object.entries(blockRulesMapping).reduce(
    (acc, [blockId, { logic, rule, copy_settings, block }]) => {
      const { type } = logic;
      if (type === "simple") {
        if (!rule) {
          throw new Error(
            "Malformed prepopulation logic, missing rule for type simple logic"
          );
        }
        const value =
          rule.value_type === "fixed"
            ? rule.value
            : getBlockValue(designSurveys, designSurveyValues, rule.value as string, rule.id_design_service);

        if (value === null || value === undefined)
          // In case of dynamically retrieved values, value could be null or undefined (e.g. if the source output is
          // not active). In this case, we don't want to generate an artificial response - we skip this question
          return acc;

        return {
          ...acc,
          [blockId]: { value },
        };
      } else if (type === "recursive_copy") {
        if (!copy_settings) {
          throw new Error(
            "Malformed prepopulation logic, missing copy_settings for type recursive_copy logic"
          );
        }
        /*
        HUBSPOT TICKET https://app.hubspot.com/contacts/8760420/record/0-5/19258367603/
        when the recursive copy settings are empty, we should not return anything
        before of this fix an empty array was returned.
        making the backend create auto-generated empty responses for recursive
        questions for the quotation survey. This led to a bug when creating analisi prezzi
        of those surveys.
         */
        const value = getRecursiveCopySettingsValue(
          copy_settings,
          designSurveys
        );
        return {
          ...acc,
          ...(value.recursive_questions_mapping.length > 0 ? { [blockId]: value } : {}),
        };
      } else {
        throw new Error("Malformed prepopulation logic, unknown type");
      }
    },
    {}
  );

  return {
    quotations_questions_values,
    source_surveys_ids: designSurveys.map((ss) => ss.id),
  };
}

function getBlockValue(
  designSurveys: ServiceSurveysWithIdSchema[],
  designSurveysValues: {[key: number]: SurveyOutputsType},
  value: string,
  id_design_service: number
) {
  const survey = containsServiceWithId(designSurveys, id_design_service);
  if (!survey) {
    console.warn({
      message: "Missing source survey for current rule - skipping",
      context: "Simple logic, dynamic copy",
      designServiceId: id_design_service,
      designSurveysIds: designSurveys.map(({id}) => id),
    });
    return null;
  }
  const designValues = designSurveysValues[survey.id];
  if (!designValues) {
    throw new Error(
      "Malformed prepopulation logic, missing value to be transferred"
    );
  }
  return designValues[parseInt(value)];
}

function getRecursiveCopySettingsValue(
  recursive_copy_settings: QuotationSurveyRecursiveCopySettingsSchema[],
  designSurveys: ServiceSurveysWithIdSchema[]
) {
  // the object maps 1-1 to the backend schema for now
  const copy_settings_with_survey_id = recursive_copy_settings
    .map((rule) => {
      const sourceSurvey = containsServiceWithId(
        designSurveys,
        rule.id_design_service
      );
      if (!sourceSurvey) {
        console.warn({
          message: "Missing source survey for current rule - skipping",
          context: "Recursive copy",
          designServiceId: rule.id_design_service,
          designSurveysIds: designSurveys.map(({id}) => id),
        });
        return null;
      }
      return {
        ...rule,
        id_design_service_survey: sourceSurvey.id,
      };
    })
    .filter((r) => r !== null);
  return {
    recursive_questions_mapping: copy_settings_with_survey_id,
  };
}

function getPrepopulationLogic(block: ServicesConfigurationsWithIdSchema) {
  return block.design_survey_prepopulation_logic;
}


function getVersion(surveys: ServiceSurveysWithIdSchema[], id: number) {
  const survey = containsServiceWithId(surveys, id);
  if (!survey) {
    return 0
  }
  return survey.service_version as number;
}

/**
 * For each rule in this logic evaluate if it mathes
 *
 * the first fixed rule matches
 *
 * the first dynamic rule that passes the dependencies matches
 *
 * if it is a recursive_copy_settings rule it matches
 */
async function getMatchingRule(
  logic: QuotationSurveyPrepopulationLogicSchema,
  sourceSurveys: ServiceSurveysWithIdSchema[],
  getResponses: GetResponses,
  getServiceConfiguration: GetServiceConfiguration,
  getConnectionResults: GetConnectionResults,
  block: ServicesConfigurationsWithIdSchema
): Promise<
  | QuotationSurveyPrepopulationRuleSchema
  | QuotationSurveyRecursiveCopySettingsSchema[]
  | undefined
> {
  const { type, rules, recursive_copy_settings } = logic;

  if (type === "simple") {
    if (!rules) {
      throw new Error(
        "Malformed prepopulation logic, missing rule for type simple logic"
      );
    }
    let matching = undefined;
    for (const rule of rules) {
      const sourceSurvey = containsServiceWithId(
        sourceSurveys,
        rule.id_design_service
      );
      if (!sourceSurvey) {
        continue;
      }
      if (rule.dependencies) {
        const dependencies_passed = await checkDependencies(
          rule.dependencies,
          sourceSurvey,
          getResponses,
          getServiceConfiguration,
          getConnectionResults,
        );
        if (!dependencies_passed) {
          continue;
        }
      }

      matching = rule;
      break;
    }

    return matching;
  } else if (type === "recursive_copy") {
    if (!recursive_copy_settings) {
      throw new Error(
        "Malformed prepopulation logic, missing recursive_copy_settings for type recursive_copy_settings logic"
      );
    }
    const survey_configurations = Object.fromEntries(
      await Promise.all(
        recursive_copy_settings.map(async (rule) => {
          const configuration = await getServiceConfiguration(
            rule.id_design_service,
            getVersion(sourceSurveys, rule.id_design_service)
          );
          return [rule.id_design_service, configuration];
        })
      )
    );

    const matching = recursive_copy_settings
      .map((rule: QuotationSurveyRecursiveCopySettingsSchema) => {
        if (
          survey_configurations[rule.id_design_service]?.find(
            (block: any) => block.id === rule.id_source_recursive_question
          )
        ) {
          return rule;
        }
        return undefined;
      })
      .filter(
        (r: QuotationSurveyRecursiveCopySettingsSchema | undefined) =>
          r !== undefined
      );
    return matching as QuotationSurveyRecursiveCopySettingsSchema[];
  } else {
    throw new Error("Malformed prepopulation logic, unknown type");
  }
}

function containsServiceWithId(
  surveys: ServiceSurveysWithIdSchema[],
  id: number
) {
  return surveys.find((ss) => ss.service_id === id);
}

function containsSurveyWithId(
  surveys: ServiceSurveysWithIdSchema[],
  id: number
) {
  return surveys.find((ss) => ss.id === id);
}

async function checkDependencies(
  dependencies: string[],
  sourceSurvey: ServiceSurveysWithIdSchema,
  getResponses: GetResponses,
  getServiceConfiguration: GetServiceConfiguration,
  getConnectionResults: GetConnectionResults
) {
  const responses = await getResponses(sourceSurvey.id);
  const configuration = await getServiceConfiguration(
    sourceSurvey.service_id as number,
    sourceSurvey.service_version as number
  );
  const connectionResults = await getConnectionResults(sourceSurvey.id);
  const context = {
    data: prepareConfigurationStep(configuration, undefined, sourceSurvey.service_id),
    responses: {},
    configuration: configuration,
    connectionResults: connectionResults,
  };
  const convertedResponses = convertSurveyResponses(responses, context);
  context["responses"] = convertedResponses;
  return evaluateBlockDependencies(dependencies, context);
}
